import { useCallback, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { procedureStatusColorsQuery } from '@/graph/procedureStatusColors';
import {
  getLegacyAnesthesiologistColor,
  getLegacyPanelColor,
  getLegacyScheduleColor,
} from '@/util/legacyStatusColors';

export type StatusColorVariant = 'schedule' | 'anesthesiologist' | 'panel';

type StatusColorMap = Record<string, string>;

/**
 * Returns a getColor(status, hospitalId?, defaultColor?) function.
 * Uses the hospital's backend procedureStatusColors when non-empty;
 * otherwise falls back to the legacy hostname+hospitalId maps for the given variant.
 */
export function useStatusColor(variant: StatusColorVariant) {
  const { data } = useQuery(procedureStatusColorsQuery);

  const backendMap: StatusColorMap | null = useMemo(() => {
    const rows = data?.procedureStatusColors;
    // Empty / unset config means colors are off — fall back to legacy maps.
    if (!Array.isArray(rows) || rows.length === 0) {
      return null;
    }
    return rows.reduce<StatusColorMap>((acc, row) => {
      if (row?.status && row?.color) {
        acc[row.status] = row.color;
      }
      return acc;
    }, {});
  }, [data?.procedureStatusColors]);

  const useBackend = Boolean(backendMap);

  return useCallback(
    (status: string, hospitalId?: number, defaultColor?: string | null) => {
      if (useBackend && backendMap) {
        if (backendMap[status] != null) {
          return backendMap[status];
        }
        if (variant === 'panel') {
          return defaultColor === undefined ? null : defaultColor;
        }
        return defaultColor === undefined ? '#00A7F7' : defaultColor;
      }

      switch (variant) {
        case 'anesthesiologist':
          return getLegacyAnesthesiologistColor(status, hospitalId, defaultColor);
        case 'panel':
          return getLegacyPanelColor(status, hospitalId);
        case 'schedule':
        default:
          return getLegacyScheduleColor(status, hospitalId, defaultColor);
      }
    },
    [backendMap, useBackend, variant]
  );
}
